import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

// ============ Types ============

export interface MFAEnrollmentStatus {
  id: string;
  user_id: string;
  is_enrolled: boolean;
  enrolled_at: string | null;
  last_verified_at: string | null;
  recovery_codes_generated_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface MFAFactor {
  id: string;
  friendly_name: string;
  factor_type: 'totp';
  status: 'verified' | 'unverified';
}

export interface TOTPEnrollment {
  id: string;
  type: 'totp';
  totp: {
    qr_code: string;
    secret: string;
    uri: string;
  };
}

// ============ Query Keys ============

export const mfaKeys = {
  all: ['mfa'] as const,
  status: (userId: string) => [...mfaKeys.all, 'status', userId] as const,
  factors: () => [...mfaKeys.all, 'factors'] as const,
};

// ============ Hooks ============

/**
 * Hook to get MFA enrollment status from database
 */
export function useMFAStatus(userId: string | undefined) {
  return useQuery({
    queryKey: mfaKeys.status(userId || ''),
    queryFn: async () => {
      if (!userId) return null;
      
      const { data, error } = await supabase
        .from('mfa_enrollment_status')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (error) throw error;
      return data as MFAEnrollmentStatus | null;
    },
    enabled: !!userId,
  });
}

/**
 * Hook to get user's MFA factors from Supabase Auth
 */
export function useMFAFactors() {
  return useQuery({
    queryKey: mfaKeys.factors(),
    queryFn: async () => {
      const { data, error } = await supabase.auth.mfa.listFactors();
      
      if (error) throw error;
      return data.totp as MFAFactor[];
    },
  });
}

/**
 * Hook to check if MFA challenge is required
 */
export function useMFARequired() {
  const [isRequired, setIsRequired] = useState(false);
  const [factorId, setFactorId] = useState<string | null>(null);

  const checkMFA = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setIsRequired(false);
      return false;
    }

    // Check assurance level
    const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    
    if (error) {
      console.error('Error checking MFA level:', error);
      return false;
    }

    // If user has MFA enrolled but current level is aal1, require MFA
    if (data.currentLevel === 'aal1' && data.nextLevel === 'aal2') {
      setIsRequired(true);
      // Get the first TOTP factor
      const { data: factors } = await supabase.auth.mfa.listFactors();
      if (factors?.totp?.[0]) {
        setFactorId(factors.totp[0].id);
      }
      return true;
    }

    setIsRequired(false);
    return false;
  }, []);

  return { isRequired, factorId, checkMFA };
}

/**
 * Hook to enroll in TOTP MFA
 */
export function useEnrollMFA() {
  const _queryClient = useQueryClient();
  const { t } = useTranslation('common');

  return useMutation<TOTPEnrollment, Error, string | undefined>({
    mutationFn: async (friendlyName) => {
      // Enroll in TOTP
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: friendlyName || 'Authenticator App',
      });

      if (error) throw error;
      return data as TOTPEnrollment;
    },
    onError: (error) => {
      console.error('MFA enrollment error:', error);
      toast.error(t('mfa.enrollError', 'Failed to start MFA enrollment'));
    },
  });
}

/**
 * Hook to verify TOTP during enrollment
 */
export function useVerifyMFAEnrollment() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');

  return useMutation({
    mutationFn: async ({ factorId, code }: { factorId: string; code: string }) => {
      // Challenge to get the challenge ID
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });

      if (challengeError) throw challengeError;

      // Verify with the code
      const { data, error } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code,
      });

      if (error) throw error;

      // Update enrollment status in database
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('mfa_enrollment_status')
          .update({
            is_enrolled: true,
            enrolled_at: new Date().toISOString(),
            last_verified_at: new Date().toISOString(),
          })
          .eq('user_id', user.id);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mfaKeys.all });
      toast.success(t('mfa.enrollSuccess', 'Two-factor authentication enabled successfully'));
    },
    onError: (error: any) => {
      console.error('MFA verification error:', error);
      const message = error.message?.includes('Invalid')
        ? t('mfa.invalidCode', 'Invalid verification code')
        : t('mfa.verifyError', 'Failed to verify code');
      toast.error(message);
    },
  });
}

/**
 * Hook to verify TOTP during login challenge
 */
export function useVerifyMFAChallenge() {
  const { t } = useTranslation('common');

  return useMutation({
    mutationFn: async ({ factorId, code }: { factorId: string; code: string }) => {
      // Create a challenge
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });

      if (challengeError) throw challengeError;

      // Verify
      const { data, error } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code,
      });

      if (error) throw error;

      // Update last verified timestamp
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('mfa_enrollment_status')
          .update({ last_verified_at: new Date().toISOString() })
          .eq('user_id', user.id);

        // Log session event
        await supabase.rpc('log_session_event', {
          p_user_id: user.id,
          p_event_type: 'mfa_success',
        });
      }

      return data;
    },
    onError: async (error: any) => {
      console.error('MFA challenge verification error:', error);
      
      // Log failed attempt
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.rpc('log_session_event', {
          p_user_id: user.id,
          p_event_type: 'mfa_failure',
        });
      }

      const message = error.message?.includes('Invalid')
        ? t('mfa.invalidCode', 'Invalid verification code')
        : t('mfa.challengeError', 'Failed to verify code');
      toast.error(message);
    },
  });
}

/**
 * Hook to unenroll from MFA
 */
export function useUnenrollMFA() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');

  return useMutation({
    mutationFn: async (factorId: string) => {
      const { error } = await supabase.auth.mfa.unenroll({
        factorId,
      });

      if (error) throw error;

      // Update enrollment status in database
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('mfa_enrollment_status')
          .update({
            is_enrolled: false,
            enrolled_at: null,
          })
          .eq('user_id', user.id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mfaKeys.all });
      toast.success(t('mfa.unenrollSuccess', 'Two-factor authentication disabled'));
    },
    onError: (error) => {
      console.error('MFA unenroll error:', error);
      toast.error(t('mfa.unenrollError', 'Failed to disable two-factor authentication'));
    },
  });
}
